import { BOUND_OPTIONS } from 'SharedDir/service/dadata/field-address-dadata';
import {
  FIELD_TYPE_INPUT_TEXT,
  FIELD_TYPE_INPUT_NUMBER,
  FIELD_TYPE_INPUT_DATE,
  FIELD_TYPE_CHECKBOX,
  FIELD_TYPE_SELECT,
  FIELD_TYPE_FILE,
  FIELD_TYPE_TEXTAREA,
  FIELD_TYPE_SAMPLE_FILE,
  FIELD_TYPE_FILE_SET,
  FIELD_TYPE_ADDRESS,
  FIELD_TYPE_LABEL,
  FIELD_TYPE_FIXED_TABLE,
  FIELD_TYPE_DYNAMIC_TABLE,
  FIELD_TYPE_PICTURE,
  FIELD_TYPE_PICTURE_GALLERY,
  FIELD_TYPE_WYSIWYG,
  FIELD_TYPE_VIDEO,
  FIELD_TYPE_GEOLOCATION,
  FIELD_TYPE_TREE_SELECT,
  FIELD_TYPE_TREE,
  FIELD_TYPE_SEPARATOR,
  FIELD_TYPE_OBJECT,
} from 'SharedDir/constants/field-types';
import { FieldParameter } from 'SharedDir/declarations/fields';
import { getVideoLinkValidationError } from 'SharedDir/service/fields/fields';

/**
 * Генерируем объект со значениями по-умолчанию для набора доп параметров поля
 *
 * @param paramSet
 * @returns
 */
export const makeDefaultParams = (paramSet: Array<FieldParameter>): Record<string, unknown> => {
  const entries = paramSet.map(({ name, defaultValue }) => {
    let value: unknown;
    if (name === 'default') {
      value = { value: null };
    } else {
      value = typeof defaultValue !== 'undefined' ? defaultValue : null;
    }
    return [name, value];
  });
  return Object.fromEntries(entries);
};

export const TOOLTIP: FieldParameter = {
  name: 'tooltip',
  label: 'Подсказка, выводимая при наведении на поле',
  type: 'text',
  isOptional: true,
};

export const PLACEHOLDER: FieldParameter = {
  name: 'placeholder',
  label: 'Подсказка выводимая в поле',
  type: 'text',
  isOptional: true,
};

export const DEFAULT_VALUE = (type: number): FieldParameter => ({
  name: 'default',
  isOptional: true,
  label: 'Значение по-умолчанию',
  type,
});

export const WITH_DEFAULT_VALUE = (
  type: number,
  params: Array<FieldParameter>
): Array<FieldParameter> => [DEFAULT_VALUE(type), TOOLTIP, ...params];

export const TEXT_PARAMS: Array<FieldParameter> = [
  PLACEHOLDER,
  { name: 'minLength', label: 'Минимальная длина', type: 'number', isOptional: true },
  { name: 'maxLength', label: 'Максимальная длина', type: 'number', isOptional: true },
];

export const TEXTAREA_PARAMS: Array<FieldParameter> = [
  ...TEXT_PARAMS,
  {
    name: 'autogrow',
    label: 'Автоматически увеличивать размер окна в соответствии с размером текста',
    type: 'boolean',
    isOptional: true,
  },
  { name: 'rowsCount', label: 'Высота поля в строках', type: 'number', isOptional: true },
];

export const NUMBER_PARAMS: Array<FieldParameter> = [
  { name: 'min', label: 'Минимальное значение', type: 'number', isOptional: true },
  { name: 'max', label: 'Максимальное значение', type: 'number', isOptional: true },
  PLACEHOLDER,
  {
    name: 'isFloat',
    label: 'Десятичное число',
    type: 'boolean',
    defaultValue: false,
    isOptional: true,
  },
];

export const DATE_PARAMS: Array<FieldParameter> = [
  { name: 'min', label: 'Минимальное значение', type: 'date', isOptional: true },
  { name: 'max', label: 'Максимальное значение', type: 'date', isOptional: true },
  {
    name: 'anchorDate',
    label: 'Отправная точка для относительных ограничений',
    type: 'date',
    isOptional: true,
  },
  {
    name: 'pastRestriction',
    label: 'Относительное ограничение (прошлое)',
    type: 'dateRestriction',
    isOptional: true,
  },
  {
    name: 'futureRestriction',
    label: 'Относительное ограничение (будущее)',
    type: 'dateRestriction',
    isOptional: true,
  },
  PLACEHOLDER,
];

export const CHECKBOX_PARAMS: Array<FieldParameter> = [];

export const SELECT_PARAMS: Array<FieldParameter> = [
  {
    name: 'multiple',
    label: 'Допускается ли множественный ввод',
    type: 'boolean',
    defaultValue: false,
    isOptional: true,
  },
  {
    name: 'catalogSlug',
    label: 'Каталог, из которого будут выбираться значения',
    type: 'catalog',
    isOptional: true,
  },
  {
    name: 'maxElements',
    label: 'Предельное кол-во выбранных элементов',
    type: 'number',
    isOptional: true,
  },
  {
    name: 'useDropdown',
    label: 'Использовать выпадающий список',
    type: 'boolean',
    defaultValue: true,
    isOptional: true,
  },
  {
    name: 'useSearch',
    label: 'Использовать поиск по вариантам заполнения',
    type: 'boolean',
    defaultValue: true,
    isOptional: true,
  },
  { name: 'values', label: 'Варианты выбора', type: 'options', isOptional: true },
];

export const FILE_PARAMS: Array<FieldParameter> = [
  {
    name: 'extension',
    label: 'Допустимые типы',
    type: 'select',
    selectOptions: {
      options: [
        { value: 'image', label: 'image' },
        { value: 'audio', label: 'audio' },
        { value: 'doc', label: 'doc' },
        { value: 'pdf', label: 'pdf' },
        { value: 'table', label: 'table' },
        { value: 'any', label: 'any' },
      ],
      useDropdown: true,
      multiple: true,
    },
    isOptional: true,
  },
  { name: 'example', label: 'Шаблон', type: 'file', isOptional: true },
  {
    name: 'maxSize',
    label: 'Максимально допустимый размер файла, Кбайт',
    type: 'number',
    isOptional: true,
  },
];

export const FILE_SET_PARAMS: Array<FieldParameter> = [
  ...FILE_PARAMS,
  {
    name: 'maxCount',
    label: 'Максимальное количество файлов в наборе',
    type: 'number',
    isOptional: true,
  },
  {
    name: 'minCount',
    label: 'Минимальное количество файлов в наборе',
    type: 'number',
    isOptional: true,
  },
];

const AddressBoundHints: FieldParameter['selectOptions'] = {
  options: BOUND_OPTIONS.map((s) => ({
    value: s.value,
    label: s.label,
  })),
  multiple: false,
  useDropdown: true,
};

export const ADDRESS_PARAMS: Array<FieldParameter> = [
  {
    name: 'fromBound',
    label: 'Гранулярные подсказки от определенной части адреса',
    type: 'select',
    selectOptions: AddressBoundHints,
    defaultValue: 'region',
    isOptional: false,
  },
  {
    name: 'toBound',
    label: 'Гранулярные подсказки до определенной части адреса',
    type: 'select',
    selectOptions: AddressBoundHints,
    defaultValue: 'house',
    isOptional: false,
  },
];

export const LABEL_PARAMS: Array<FieldParameter> = [
  {
    name: 'size',
    label: 'Вариант написания текста',
    type: 'select',
    selectOptions: {
      options: [
        { value: 'normal', label: 'normal' },
        { value: 'header', label: 'header' },
      ],
      multiple: false,
      useDropdown: true,
    },
    defaultValue: 'normal',
    isOptional: true,
  },
  { name: 'bold', label: 'Жирный', type: 'boolean', defaultValue: false, isOptional: true },
  { name: 'italic', label: 'Курсив', type: 'boolean', defaultValue: false, isOptional: true },
  { name: 'text', label: 'Выводимый текст', type: 'text', isOptional: true },
];

export const FIXED_TABLE_PARAMS: Array<FieldParameter> = [
  { name: 'caption', label: 'Заголовок таблицы', type: 'text', isOptional: true },
  { name: 'rows', label: 'Описание строк таблицы', type: 'tableRows', isOptional: true },
  {
    name: 'cols',
    label: 'Описание столбцов таблицы',
    type: 'tableColumns',
    isOptional: true,
    extra: {},
  },
  { name: 'firstColumnHeader', label: 'Заголовок первого столбца', type: 'text', isOptional: true },
];

export const DYNAMIC_TABLE_PARAMS: Array<FieldParameter> = [
  { name: 'caption', label: 'Заголовок таблицы', type: 'text', isOptional: true },
  {
    name: 'cols',
    label: 'Описание столбцов таблицы',
    type: 'tableColumns',
    isOptional: true,
    extra: { dontSumFirst: true },
  },
  { name: 'useTotalRow', label: 'Итоговая строка', type: 'boolean', isOptional: true },
  {
    name: 'totalRowTitle',
    label: 'Наименование итоговой строки',
    type: 'text',
    defaultValue: 'Итого',
    isOptional: true,
  },
];

export const PICTURE_PARAMS: Array<FieldParameter> = [
  { name: 'alt', label: 'Альтернативный текст', type: 'text', isOptional: true },
  { name: 'fileId', label: 'Изображение', type: 'file', isOptional: true },
];

export const PICTURE_GALLERY_PARAMS: Array<FieldParameter> = [
  {
    name: 'files',
    label: 'Набор изображений',
    type: 'arrayOf',
    itemType: PICTURE_PARAMS,
    isOptional: true,
  },
];

export const WYSIWYG_PARAMS: Array<FieldParameter> = [
  { name: 'text', label: 'Выводимый текст', type: 'wysiwyg', isOptional: true },
];

export const VIDEO_PARAMS: Array<FieldParameter> = [
  {
    name: 'link',
    label: 'Ссылка на видео',
    type: 'text',
    isOptional: true,
    rules: [(val) => getVideoLinkValidationError(val) || true],
  },
];

export const GEOLOCATION_PARAMS: Array<FieldParameter> = [
  {
    name: 'coords',
    label: 'Широта и долгота координаты точки, на которую будет указывать карта',
    type: 'geolocation',
    isOptional: true,
  },
];

export const TREE_SELECT_PARAMS: Array<FieldParameter> = [
  {
    name: 'multiple',
    label: 'Допускается ли множественный ввод',
    type: 'boolean',
    defaultValue: false,
    isOptional: true,
  },
  {
    name: 'catalogSlug',
    label: ' Каталог из которого будут браться значения',
    type: 'catalog',
    isOptional: true,
  },
  {
    name: 'maxElements',
    label: 'Максимальное кол-во вариантов выбора',
    type: 'number',
    isOptional: true,
  },
];

export const TREE_PARAMS: Array<FieldParameter> = [
  { name: 'title', label: 'Заголовок', type: 'text', isOptional: true },
];

export const FILE_SAMPLE_PARAMS: Array<FieldParameter> = [
  { name: 'fileId', label: 'Образец', type: 'file', isOptional: true },
  { name: 'title', label: 'Заголовок', type: 'text', isOptional: true },
];

export const SEPARATOR_PARAMS: Array<FieldParameter> = [
  {
    name: 'height',
    label: 'Размер',
    type: 'select',
    selectOptions: {
      options: [
        { value: 'small', label: 'small' },
        { value: 'medium', label: 'medium' },
        { value: 'big', label: 'big' },
      ],
      multiple: false,
      useDropdown: true,
    },
    defaultValue: 'medium',
    isOptional: true,
  },
];

/**
 * Чисто для внутреннего пользования
 */
export const ALL_KIND_OF_PARAMS: Array<FieldParameter> = [
  { name: 'text', label: 'Параметр - текст', type: 'text', isOptional: true },
  { name: 'number', label: 'Параметр - число', type: 'number', isOptional: true },
  { name: 'textarea', label: 'Параметр - текстовая область', type: 'textarea', isOptional: true },
  {
    name: 'boolean',
    label: 'Параметр - переключатель',
    type: 'boolean',
    defaultValue: false,
    isOptional: true,
  },
  { name: 'date', label: 'Параметр - дата', type: 'date', isOptional: true },
  {
    name: 'dateRestriction',
    label: 'Параметр - ограничение даты',
    type: 'dateRestriction',
    isOptional: true,
  },
  { name: 'catalog', label: 'Параметр - каталог', type: 'catalog', isOptional: true },
  { name: 'options', label: 'Параметр - опции', type: 'options', isOptional: true },
  { name: 'file', label: 'Параметр - файл', type: 'file', isOptional: true },
  {
    name: 'arrayOf',
    label: 'Параметр - множество изображений',
    type: 'arrayOf',
    itemType: PICTURE_PARAMS,
    isOptional: true,
  },
  { name: 'wysiwyg', label: 'Параметр - текстовый редактор', type: 'wysiwyg', isOptional: true },
  { name: 'geolocation', label: 'Параметр - геопозиция', type: 'geolocation', isOptional: true },
  {
    name: 'select',
    label: 'Параметр - выбор из множества',
    type: 'select',
    selectOptions: AddressBoundHints,
    isOptional: true,
  },
  {
    name: 'size',
    label: 'Параметр - выбор из множества (без выпадающего списка)',
    type: 'select',
    selectOptions: {
      options: [
        { value: 'normal', label: 'Обычный' },
        { value: 'header', label: 'Крупный' },
      ],
      multiple: true,
      useDropdown: false,
    },
    defaultValue: ['normal'],
    isOptional: true,
  },
  {
    name: 'tableRows',
    label: 'Параметр - строки таблицы',
    type: 'tableRows',
    defaultValue: [
      {
        label: 'Строка 1',
        slug: 'row1',
        order: 0,
      },
      {
        label: 'Строка 2',
        slug: 'row2',
        order: 1,
      },
      {
        label: 'Строка 3',
        slug: 'row3',
        order: 2,
      },
      {
        label: 'Строка 4',
        slug: 'row4',
        order: 3,
      },
    ],
    isOptional: true,
  },
  {
    name: 'tableColumns',
    label: 'Параметр - столбцы таблицы',
    type: 'tableColumns',
    defaultValue: {
      columns: [
        {
          slug: 'var1',
          order: 0,
        },
        {
          slug: 'var2',
          order: 1,
        },
        {
          slug: 'var3',
          order: 2,
        },
        {
          slug: 'var4',
          order: 3,
        },
      ],
      headers: [
        [
          { label: 'Заголовок 1', colspan: 2 },
          { label: 'Заголовок 2', order: 1 },
        ],
      ],
    },
    extra: { dontSumFirst: true },
    isOptional: true,
  },
];

export default {
  [FIELD_TYPE_INPUT_TEXT.id]: WITH_DEFAULT_VALUE(FIELD_TYPE_INPUT_TEXT.id, TEXT_PARAMS),
  [FIELD_TYPE_INPUT_NUMBER.id]: WITH_DEFAULT_VALUE(FIELD_TYPE_INPUT_NUMBER.id, NUMBER_PARAMS),
  [FIELD_TYPE_INPUT_DATE.id]: WITH_DEFAULT_VALUE(FIELD_TYPE_INPUT_DATE.id, DATE_PARAMS),
  [FIELD_TYPE_CHECKBOX.id]: WITH_DEFAULT_VALUE(FIELD_TYPE_CHECKBOX.id, CHECKBOX_PARAMS),
  [FIELD_TYPE_SELECT.id]: WITH_DEFAULT_VALUE(FIELD_TYPE_SELECT.id, SELECT_PARAMS),
  [FIELD_TYPE_TEXTAREA.id]: WITH_DEFAULT_VALUE(FIELD_TYPE_TEXTAREA.id, TEXTAREA_PARAMS),
  [FIELD_TYPE_FILE.id]: WITH_DEFAULT_VALUE(FIELD_TYPE_FILE.id, FILE_PARAMS),
  [FIELD_TYPE_FILE_SET.id]: WITH_DEFAULT_VALUE(FIELD_TYPE_FILE_SET.id, FILE_SET_PARAMS),
  [FIELD_TYPE_ADDRESS.id]: WITH_DEFAULT_VALUE(FIELD_TYPE_ADDRESS.id, ADDRESS_PARAMS),
  [FIELD_TYPE_LABEL.id]: LABEL_PARAMS,
  [FIELD_TYPE_FIXED_TABLE.id]: FIXED_TABLE_PARAMS,
  [FIELD_TYPE_DYNAMIC_TABLE.id]: DYNAMIC_TABLE_PARAMS,
  [FIELD_TYPE_PICTURE.id]: PICTURE_PARAMS,
  [FIELD_TYPE_PICTURE_GALLERY.id]: PICTURE_GALLERY_PARAMS,
  [FIELD_TYPE_WYSIWYG.id]: WYSIWYG_PARAMS,
  [FIELD_TYPE_VIDEO.id]: VIDEO_PARAMS,
  [FIELD_TYPE_GEOLOCATION.id]: GEOLOCATION_PARAMS,
  [FIELD_TYPE_TREE_SELECT.id]: WITH_DEFAULT_VALUE(FIELD_TYPE_TREE_SELECT.id, TREE_SELECT_PARAMS),
  [FIELD_TYPE_TREE.id]: TREE_PARAMS,
  [FIELD_TYPE_SAMPLE_FILE.id]: FILE_SAMPLE_PARAMS,
  [FIELD_TYPE_SEPARATOR.id]: SEPARATOR_PARAMS,
  [FIELD_TYPE_OBJECT.id]: [],
} as Record<number, Array<FieldParameter>>;
